import { useAuth } from '@clerk/react-router'
import { Link, useLocation } from 'react-router-dom'
import { useHotkey } from '@tanstack/react-hotkeys'
import { parseAsBoolean, useQueryState } from 'nuqs'
import type { ReactElement, ReactNode } from 'react'
import { useState } from 'react'
import { UserRoleSchema } from '@joyful/utils'

import type { ItemProps } from '../../../core'
import {
  Button,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRawItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  IconButton,
  Item,
  ItemContent,
  ItemSubtitle,
  Keyboard,
  Stack,
  Tooltip,
  UserAvatar,
  toast,
} from '../../../core'
import { GlobalHotkeys } from '../../utils/hotkeys'
import type { AppNavigationItem } from '../global-command/GlobalCommandCore'
import {
  getAppNavigationLinkTo,
  useGlobalCommand,
} from '../global-command/GlobalCommandCore'
import { getNavbarAppHref } from './Navbar'
import {
  UserDropdownMenu,
  useUserDropdownMenu,
} from '../UserDropdownMenuContext'
import { DeploymentInfo } from '../DeploymentInfo'
import { RestrictToCapability } from '../RestrictToCapability'
import { HelpActionMenuItems } from '../../features/feedback/components/HelpActionMenuItems'
import { useActiveAccount } from '../../utils/activeAccountContext'
import { useCurrentUser } from '../../utils/currentUserContext'
import { useApiQueryClient } from '../../utils/reactQuery'
import { DisplaySettingsDialog } from '../../features/admin/components/DisplaySettingsDialog'
import { setImpersonatedRole } from '../../features/admin/utils/roleImpersonation'

export interface NavbarUserMenuProps {
  accountGroupLabel?: ReactNode
  accountItems?: ReadonlyArray<AppNavigationItem>
  currentUserSubmenuItems?: ReactNode
  frontendCommitMessage?: string | null
  frontendCommitSha?: string | null
  frontendCommitTimestamp?: string | null
  generalItems?: ReadonlyArray<AppNavigationItem>
  helpMenuItem?: ReactNode
  renderTrigger?: (trigger: ReactElement) => ReactNode
  setDemoMode?: (demoModeAccountSlug: string | null) => void
  utilityMenuItems?: ReactNode
}

export function NavbarUserMenu({
  accountGroupLabel,
  accountItems = [],
  currentUserSubmenuItems,
  frontendCommitMessage = null,
  frontendCommitSha = null,
  frontendCommitTimestamp = null,
  generalItems = [],
  helpMenuItem,
  renderTrigger,
  setDemoMode,
  utilityMenuItems,
}: NavbarUserMenuProps) {
  const currentUser = useCurrentUser()
  const currentUserRoleMeta = UserRoleSchema.meta(currentUser.role)
  const impersonatedRoleMeta = currentUser.impersonated_role
    ? UserRoleSchema.meta(currentUser.impersonated_role)
    : null
  const activeAccount = useActiveAccount()
  const globalCommand = useGlobalCommand()
  const [isDisplaySettingsOpen, setDisplaySettingsOpen] = useQueryState(
    'display',
    parseAsBoolean.withDefault(false),
  )
  const resolvedAccountGroupLabel = accountGroupLabel ?? activeAccount?.name
  const accountHref = getNavbarAppHref({ app: 'app', path: '/account' })
  useHotkey(GlobalHotkeys.displaySettings, () => {
    void setDisplaySettingsOpen(true)
  })
  const trigger = (
    <DropdownMenuTrigger asChild>
      <IconButton
        variant="ghost"
        size="lg"
        colorVariant="emphasis"
        aria-label={
          impersonatedRoleMeta
            ? `Impersonating ${impersonatedRoleMeta.title}`
            : undefined
        }
      >
        <CurrentUserAvatar size="lg" />
      </IconButton>
    </DropdownMenuTrigger>
  )
  const renderedTrigger = renderTrigger ? renderTrigger(trigger) : trigger
  const triggerNode = impersonatedRoleMeta ? (
    <RoleImpersonationClearHoverCard
      impersonatedRoleTitle={impersonatedRoleMeta.title}
    >
      {renderedTrigger}
    </RoleImpersonationClearHoverCard>
  ) : (
    renderedTrigger
  )

  return (
    <UserDropdownMenu>
      {triggerNode}

      <DropdownMenuContent
        className="max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)] w-72"
        align="end"
      >
        <DropdownMenuRawItem asChild>
          <CurrentUserItem accountHref={accountHref} />
        </DropdownMenuRawItem>

        {currentUserRoleMeta.canImpersonateRoles && currentUserSubmenuItems ? (
          <>
            <DropdownMenuSeparator />
            {currentUserSubmenuItems}
          </>
        ) : null}

        <DropdownMenuSeparator />

        {accountItems.length > 0 ? (
          <>
            <DropdownMenuGroup>
              {resolvedAccountGroupLabel ? (
                <DropdownMenuLabel className="px-2 py-1">
                  {resolvedAccountGroupLabel}
                </DropdownMenuLabel>
              ) : null}
              <NavbarUserMenuItems items={accountItems} />
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {generalItems.length > 0 ? (
          <>
            <DropdownMenuGroup>
              <NavbarUserMenuItems items={generalItems} />
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
          </>
        ) : null}

        <DropdownMenuGroup>
          {utilityMenuItems}
          <DropdownMenuItem
            shouldCloseOnSelect
            endAdornment={<Keyboard>{GlobalHotkeys.displaySettings}</Keyboard>}
            onSelect={() => {
              void setDisplaySettingsOpen(true)
            }}
          >
            {`Display settings…`}
          </DropdownMenuItem>
          <DropdownMenuItem
            endAdornment={<Keyboard>{GlobalHotkeys.globalCommand}</Keyboard>}
            shouldCloseOnSelect
            onSelect={() => {
              globalCommand.open()
            }}
          >
            {`Command menu`}
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          {helpMenuItem ?? <HelpDropdownMenuItem />}

          <DropdownMenuRawItem asChild>
            <SignOutItem />
          </DropdownMenuRawItem>
        </DropdownMenuGroup>

        <RestrictToCapability capability="deployment.view">
          <DropdownMenuSeparator />
          <DeploymentInfo
            frontendCommitMessage={frontendCommitMessage}
            frontendCommitSha={frontendCommitSha}
            frontendCommitTimestamp={frontendCommitTimestamp}
          />
        </RestrictToCapability>
      </DropdownMenuContent>

      <DisplaySettingsDialog
        open={isDisplaySettingsOpen}
        onOpenChange={(open) => {
          void setDisplaySettingsOpen(open)
        }}
        setDemoMode={setDemoMode}
      />
    </UserDropdownMenu>
  )
}

interface RoleImpersonationClearHoverCardProps {
  children: ReactNode
  impersonatedRoleTitle: string
}

function RoleImpersonationClearHoverCard({
  children,
  impersonatedRoleTitle,
}: RoleImpersonationClearHoverCardProps) {
  const currentUser = useCurrentUser()
  const userDropdownMenu = useUserDropdownMenu()
  const { queryClient } = useApiQueryClient()
  const [isHoverCardOpen, setHoverCardOpen] = useState(false)
  const isUserDropdownMenuOpen = userDropdownMenu.state.isUserDropdownMenuOpen

  const handleClearRoleImpersonation = () => {
    const realRoleTitle = UserRoleSchema.meta(currentUser.role).title
    setHoverCardOpen(false)
    setImpersonatedRole(null)
    toast.promise(queryClient.invalidateQueries(), {
      error: `Failed to refresh role`,
      loading: `Switching role to ${realRoleTitle}…`,
      success: `Switched role to ${realRoleTitle}.`,
    })
  }

  return (
    <HoverCard
      open={isUserDropdownMenuOpen ? false : isHoverCardOpen}
      onOpenChange={(open) => {
        if (!isUserDropdownMenuOpen) {
          setHoverCardOpen(open)
        }
      }}
      openDelay={200}
      closeDelay={100}
    >
      <HoverCardTrigger asChild>
        <span className="inline-flex">{children}</span>
      </HoverCardTrigger>
      <HoverCardContent align="end" padding="compact" size="auto">
        <Stack gap="sm">
          <p className="text-tremor-content text-sm">
            {`Impersonating ${impersonatedRoleTitle}`}
          </p>
          <Button size="sm" onClick={handleClearRoleImpersonation}>
            {`Clear`}
          </Button>
        </Stack>
      </HoverCardContent>
    </HoverCard>
  )
}

function NavbarUserMenuItems({
  items,
}: {
  readonly items: ReadonlyArray<AppNavigationItem>
}) {
  const { pathname } = useLocation()

  return items.map((item) => {
    if (item.type === 'group') {
      return (
        <DropdownMenuSub key={item.key}>
          <DropdownMenuSubTrigger>{item.title}</DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="min-w-48">
              <NavbarUserMenuItems items={item.items} />
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      )
    }

    return item.path ? (
      <DropdownMenuItem
        key={item.key}
        shouldCloseOnSelect
        asChild
        isSelected={
          pathname === item.path || pathname.startsWith(`${item.path}/`)
        }
        selectionVariant="muted"
      >
        {item.href ? (
          <a href={item.href}>{item.title}</a>
        ) : (
          <Link to={getAppNavigationLinkTo(item)}>{item.title}</Link>
        )}
      </DropdownMenuItem>
    ) : (
      <DropdownMenuItem
        key={item.key}
        shouldCloseOnSelect
        onSelect={item.onSelect}
      >
        {item.title}
      </DropdownMenuItem>
    )
  })
}

interface CurrentUserItemProps extends Omit<ItemProps, 'asChild'> {
  accountHref: string
}

function CurrentUserItem({ accountHref, ...props }: CurrentUserItemProps) {
  const currentUser = useCurrentUser()
  const roleMeta = UserRoleSchema.meta(currentUser.effective_role)
  const isImpersonatingRole = currentUser.impersonated_role ? true : false

  return (
    <Item asChild isInteractive {...props}>
      <a href={accountHref}>
        <span className="mt-1 self-start">
          <CurrentUserAvatar size="md" />
        </span>
        <ItemContent>
          <p className="font-medium">{currentUser.name ?? currentUser.email}</p>
          {currentUser.name && currentUser.email ? (
            <ItemSubtitle>{currentUser.email}</ItemSubtitle>
          ) : null}
          <Tooltip title={roleMeta.title} content={roleMeta.description}>
            {isImpersonatingRole ? (
              <ItemSubtitle asChild tone="inherit" weight="medium">
                <span className="text-tremor-brand-emphasis">
                  {`Impersonating ${roleMeta.title}`}
                </span>
              </ItemSubtitle>
            ) : (
              <ItemSubtitle>{roleMeta.title}</ItemSubtitle>
            )}
          </Tooltip>
        </ItemContent>
      </a>
    </Item>
  )
}

interface CurrentUserAvatarProps {
  size?: 'sm' | 'md' | 'lg'
}

function CurrentUserAvatar({ size = 'md' }: CurrentUserAvatarProps) {
  const currentUser = useCurrentUser()
  const isImpersonatingRole = currentUser.impersonated_role ? true : false

  return (
    <span className="relative inline-flex">
      <UserAvatar
        userId={currentUser.id}
        imageUrl={currentUser.image_url ?? null}
        isCurrentUser
        name={currentUser.name ?? null}
        ring={isImpersonatingRole ? 'impersonating' : undefined}
        size={size}
      />
      {isImpersonatingRole ? (
        <span
          className="bg-tremor-brand border-tremor-background absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2"
          aria-hidden
        />
      ) : null}
    </span>
  )
}

function HelpDropdownMenuItem() {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{`Help`}</DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="w-72">
          <HelpActionMenuItems />
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

function SignOutItem({ ...props }: Omit<ItemProps, 'asChild'>) {
  const auth = useAuth()

  return (
    <Item
      isInteractive
      {...props}
      onClick={(event) => {
        props.onClick?.(event)
        void auth.signOut()
      }}
    >
      {`Sign out`}
    </Item>
  )
}
